import { createQuery } from "@/api/utils/createQuery";
import { MetricCode, MetricCodeWithFallbacks } from "./Metrics";
import { HydratedMetric } from "./HydratedMetric";
import { ParsedMetric, PeriodResolution } from "./Metrics.types";
import { AccountLabel } from "@/entities/Labeling/Account.model";
import { FinanceLabField } from "@/entities/FinanceLab/FinanceLab.model";
import { QueryStringBuilder } from "@/utils/filtering/QueryStringBuilder";
import { Period } from "./Period";
import { parseExpression } from "./helpers/parseMetricExpression";

export const getMetricsWithFallbacks = createQuery<
    Map<MetricCode, HydratedMetric>,
    {
        codes: MetricCodeWithFallbacks[];
        resolution: PeriodResolution;
        lineaged?: boolean;
        toDate: string;
    }
>(async ({ api, router, params }) => {
    const map: Map<MetricCode, HydratedMetric> = new Map();
    const toDate = params.toDate;

    const labels = params.codes
        .flatMap((code) => code)
        .filter((code): code is AccountLabel =>
            Object.values(AccountLabel).includes(code as AccountLabel),
        );

    if (labels.length > 0) {
        const financeMetrics = await api.get(
            `/finance/companies/${router.params.companyId}/metrics?period_kind=${params.resolution}&value_kind=${params.lineaged ? "lineage" : "plain"}&to=${toDate}${labels.reduce(
                (acc, curr) => {
                    return `${acc}&label=${curr}`;
                },
                "",
            )}`,
        );

        financeMetrics.data.forEach((metric) => {
            map.set(
                metric.info.derived.label,
                new HydratedMetric(metric, params.resolution),
            );
        });
    }

    const labLabels = params.codes.flatMap((code) => {
        const codeArray = Array.isArray(code) ? code : [code];
        const accountLabel = codeArray.find((c): c is AccountLabel =>
            Object.values(AccountLabel).includes(c as AccountLabel),
        );
        if (accountLabel && map.has(accountLabel)) {
            return [];
        }
        return codeArray.filter(
            (c): c is FinanceLabField =>
                !Object.values(AccountLabel).includes(c as AccountLabel),
        );
    });

    if (labLabels.length > 0) {
        const queryString = new QueryStringBuilder()
            .add("period_kind", params.resolution)
            .add("value_kind", "plain")
            .add("to_", toDate);

        labLabels.forEach((label) => {
            queryString.add("label", label);
        });

        const labMetrics = await api.get(
            `/hum-finance-lab/v1/companies/${router.params.companyId}/metrics?${queryString.build()}`,
        );

        labMetrics.data.forEach((metric) => {
            map.set(
                metric.info.derived.label,
                new HydratedMetric(metric, params.resolution),
            );
        });
    }

    return map;
});

export const getLineagedMetric = createQuery<
    ParsedMetric | null,
    {
        code: MetricCode;
        resolution: PeriodResolution;
        period: Period;
    }
>(async ({ api, router, params }) => {
    const metric = await api
        .get(
            `/finance/companies/${router.params.companyId}/metrics?period_kind=${params.resolution}&value_kind=lineage&label=${params.code}`,
        )
        .then(({ data }) => {
            return data.find((m) => m.info.derived.label === params.code);
        });
    console.log(metric, params.period);

    const matchedPeriod =
        metric?.lineagedValues?.find((v) => {
            console.log(
                new Period(v.period).getShortName(),
                params.period.getShortName(),
            );
            return new Period(v.period).isSameAs(params.period);
        }) || null;

    if (!matchedPeriod) {
        return null;
    }
    const { formula, values } = parseExpression(matchedPeriod.expr);
    return {
        period: params.period,
        result: matchedPeriod.value,
        formula,
        values,
    } as ParsedMetric;
});
